import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

// import { text } from 'stream/consumers';

const Form990EZInstructions = () => {
  // const [expanded, setExpanded] = useState(false);
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 990-EZ Instructions for Tax-Exempt Organizations"
          description="Learn about IRS Form 990-EZ, filing requirements, due dates, penalties, and how to complete the form for your tax-exempt organization with our detailed guide"
          keywords=""
          canonical="resources/tax-exempt/form-990-ez-instructions/"
          ogType="article"
          jsonLdPricing="9.90"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 990-EZ</span>
          </span>{' '}
          Instructions: Complete IRS Filing <br /> Guide for Nonprofits
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Everything you need to understand and complete IRS Form 990-EZ in one
          comprehensive guide.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990EZinstructions`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 4 }}
                >
                  <Box
                    itemScope
                    itemType="https://schema.org/Article"
                    sx={{
                      p: 3,
                      px: 3,
                      backgroundColor: '#f0f5f8',
                      borderLeft: '5px solid #01456c',
                      mb: 4,
                      borderRadius: 3,
                      position: 'relative',
                    }}
                  >
                    <link
                      itemProp="mainEntityOfPage"
                      href="https://www.taxzerone.com/resources/tax-exempt/form-990-ez-instructions/"
                    />

                    <div
                      itemProp="image"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                      style={{ display: 'none' }}
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>

                    <Box
                      sx={{
                        display: 'grid',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        textAlign: 'left',
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2">
                        Last updated:{' '}
                        <time
                          itemProp="datePublished"
                          dateTime="2026-07-20T09:00:00-05:00"
                        >
                          July 20, 2026
                        </time>
                        <time
                          itemProp="dateModified"
                          dateTime="2026-07-20T09:00:00-05:00"
                          style={{ display: 'none' }}
                        >
                          July 20, 2026
                        </time>
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        itemProp="author"
                        itemScope
                        itemType="https://schema.org/Person"
                      >
                        -by{' '}
                        <span itemProp="name">
                          <a
                            itemProp="url"
                            href="https://www.taxzerone.com/"
                            style={{
                              color: 'inherit',
                              textDecoration: 'none',
                            }}
                          >
                            Peter Samuel
                          </a>
                        </span>{' '}
                        | TaxZerone
                      </Typography>
                    </Box>

                    <Typography
                      variant="h4"
                      itemProp="headline"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        fontSize: { md: 20, sm: 18, xs: 16 },
                        color: '#01456c',
                        fontStyle: 'italic',
                      }}
                    >
                      Form 990-EZ at a Glance
                    </Typography>
                    <Typography
                      variant="body1"
                      itemProp="description"
                      sx={{ mb: 2 }}
                    >
                      <Link
                        href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-ez/"
                        sx={{ color: '#0202ff', textDecoration: 'underline' }}
                      >
                        IRS Form 990-EZ
                      </Link>{' '}
                      is the <b>short form</b> annual information return for
                      eligible{' '}
                      <Link
                        href="https://www.taxzerone.com/tax-exempt/"
                        sx={{ color: '#0202ff', textDecoration: 'underline' }}
                      >
                        tax-exempt
                      </Link>{' '}
                      organizations. Organizations with{' '}
                      <b>gross receipts less than $200,000</b> and{' '}
                      <b>total assets less than $500,000</b> generally qualify
                      to file Form 990-EZ instead of the standard{' '}
                      <Link
                        href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990/"
                        sx={{ color: '#0202ff', textDecoration: 'underline' }}
                      >
                        Form 990
                      </Link>
                      .
                    </Typography>

                    <Box
                      component="ul"
                      itemScope
                      itemType="https://schema.org/ItemList"
                      sx={{
                        mb: 0,
                        pl: 0,
                        listStyle: 'none',
                        '& li': { mb: 1.6, position: 'relative', pl: 3 },
                        '& li::before': {
                          content: '"★"',
                          position: 'absolute',
                          left: 0,
                          top: '-2px',
                          color: '#01456c',
                          fontSize: '18px',
                        },
                      }}
                    >
                      <li
                        itemProp="itemListElement"
                        itemScope
                        itemType="https://schema.org/ListItem"
                      >
                        <data
                          itemProp="position"
                          value="1"
                          style={{ display: 'none' }}
                        />
                        <span itemProp="name">
                          <Link
                            href="/resources/tax-exempt/form-990-due-dates/"
                            sx={{
                              color: '#0202ff',
                              textDecoration: 'underline',
                            }}
                          >
                            <b>Due Date:</b>
                          </Link>{' '}
                          <b>15th day of the 5th month</b> after the end of the
                          organization&apos;s tax year (May 15 for most
                          calendar-year filers).
                        </span>
                      </li>

                      <li
                        itemProp="itemListElement"
                        itemScope
                        itemType="https://schema.org/ListItem"
                      >
                        <data
                          itemProp="position"
                          value="2"
                          style={{ display: 'none' }}
                        />
                        <span itemProp="name">
                          <Link
                            href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                            sx={{
                              color: '#0202ff',
                              textDecoration: 'underline',
                            }}
                          >
                            <b>Extension:</b>
                          </Link>{' '}
                          File <b>IRS Form 8868</b> by the original due date to
                          receive an automatic <b>6-month extension.</b>
                        </span>
                      </li>

                      <li
                        itemProp="itemListElement"
                        itemScope
                        itemType="https://schema.org/ListItem"
                      >
                        <data
                          itemProp="position"
                          value="3"
                          style={{ display: 'none' }}
                        />
                        <span itemProp="name">
                          <Link
                            href="/resources/tax-exempt/form-990-penalties/"
                            sx={{
                              color: '#0202ff',
                              textDecoration: 'underline',
                            }}
                          >
                            <b>Late Filing Penalties:</b>
                          </Link>{' '}
                          Returns filed late or with missing information may be
                          subject to IRS penalties. Failure to file for{' '}
                          <b>three consecutive years</b> may result in the{' '}
                          <Link
                            href="/resources/tax-exempt/automatic-revocation-of-tax-exempt-status/"
                            sx={{
                              color: '#0202ff',
                              textDecoration: 'underline',
                            }}
                          >
                            automatic revocation
                          </Link>{' '}
                          of tax-exempt status.
                        </span>
                      </li>
                    </Box>

                    <div
                      itemProp="publisher"
                      itemScope
                      itemType="https://schema.org/Organization"
                      style={{ display: 'none' }}
                    >
                      <span itemProp="name">TaxZerone</span>

                      <div
                        itemProp="logo"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>
                    </div>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is IRS Form 990-EZ?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must File Form 990-EZ?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whenis" sx={{ color: '#0202ff' }}>
                          When is Form 990-EZ due?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#penalty" sx={{ color: '#0202ff' }}>
                          Form 990-EZ penalty
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          Form 990-EZ Instructions - How to fill out?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#file" sx={{ color: '#0202ff' }}>
                          How to file Form 990-EZ?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whereto" sx={{ color: '#0202ff' }}>
                          Where to mail 990 EZ tax form?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#e-file" sx={{ color: '#0202ff' }}>
                          How to e-file Form 990-EZ?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#with" sx={{ color: '#0202ff' }}>
                          E-file Form 990-EZ with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '18px !important',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.5,
                        }}
                      >
                        File your 990 series returns fast with TaxZerone and get
                        10% off—use code{' '}
                        <Box
                          component="strong"
                          sx={{
                            ml: 0,
                            color: '#c02652',
                            fontWeight: 600,
                            border: '2px dashed rgb(211, 163, 33)',
                            px: 1,
                            backgroundColor: 'rgb(255, 250, 205)',
                          }}
                        >
                          TZOTE10
                        </Box>{' '}
                        at checkout!
                      </Typography>
                      <Typography variant="body2">
                        We support tax years 2025, 2024, and 2023.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990EZinstructions`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatis"
                >
                  What is IRS Form 990-EZ?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 990-EZ filing
                  </Link>
                  , officially titled{' '}
                  <span style={{ fontWeight: 600 }}>
                    &quot; Short Form Return of Organization Exempt From Income
                    Tax,&quot;
                  </span>{' '}
                  is a simplified tax return designed for smaller tax-exempt
                  organizations with gross receipts{' '}
                  <span style={{ fontWeight: 600 }}>less than $200,000</span>{' '}
                  and total assets{' '}
                  <span style={{ fontWeight: 600 }}>less than $500,000</span> at
                  the end of their tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Form 990-EZ provides information on the organization&apos;s
                  mission, programs, and finances, offering transparency to the
                  public and the IRS about its operations.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must File Form 990-EZ?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To file Form 990-EZ, your exempt organization must meet the
                  following criteria:
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Tax-Exempt Status:</b> You must hold a valid tax-exempt
                      status under sections 501(c), 527, or 4947(a)(1) of the
                      Internal Revenue Code (except for private foundations).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Gross Receipts and Assets:</b> Your organization&apos;s{' '}
                      <b>gross receipts</b> for the year must be{' '}
                      <b>less than $200,000</b>, and your <b>total assets</b> at
                      the end of the tax year must be <b>less than $500,000</b>.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whenis"
                >
                  When is Form 990-EZ due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Exempt organizations must file Form 990-EZ on the{' '}
                  <b>15th day of the 5th month</b> after the organization&apos;s
                  tax year ends. For calendar year filers, this means Form
                  990-EZ is due on <b>May 15th</b> of the following year.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  However, if the due date falls on a weekend or a legal
                  holiday, the filing deadline is extended to the next business
                  day. Extensions can be requested if needed.
                </Typography>

                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: '18px !important',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.4,
                        }}
                      >
                        Worried about not being able to file your 990 return on
                        time?
                      </Typography>
                      <Typography variant="body2">
                        E-file Form 8868 and get an extension of up to 6 months
                        to file{' '}
                        <Hidden mdDown>
                          {' '}
                          <br />
                        </Hidden>
                        your 990-EZ return.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990EZinstructions`}
                      >
                        E-file Form 8868
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="penalty"
                >
                  Form 990-EZ penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The IRS imposes penalties based on the organization&apos;s
                  annual gross receipts:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Organizations With Gross Receipts of $1,309,500 or Less
                </Typography>
                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      The penalty is{' '}
                      <span style={{ fontWeight: 600 }}> $25 per day</span> for
                      each day the return is late.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      The maximum penalty is the{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        lesser of $13,000 or 5% of the organization’s gross
                        receipts
                      </span>{' '}
                      for the year.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Organizations With Gross Receipts Over $1,309,500
                </Typography>
                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      The penalty increases to{' '}
                      <span style={{ fontWeight: 600 }}>$130 per day</span>.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      The maximum penalty for a single return is{' '}
                      <span style={{ fontWeight: 600 }}>$65,000</span>.
                    </ListItemText>
                  </ListItem>
                </List>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> The penalty can also be charged if the
                  organization files an incomplete return, such as by failing to
                  complete a required line item or a required part of a
                  schedule.
                </Alert>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                  // className="wave-bg"
                >
                  <Box
                    sx={{ width: '100%', flexGrow: 1, mb: { xs: 1, md: 0 } }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 0,
                        fontSize: '24px !important',
                      }}
                    >
                      Don&apos;t risk penalties!
                    </Typography>
                    <Typography variant="body2">
                      Be proactive! E-file your 990-EZ ahead of the deadline.
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', minWidth: 200 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        mb: 0,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990EZinstructions`}
                    >
                      E-file Form 990-EZ
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="howto"
                >
                  Form 990-EZ Instructions - How to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Let&apos;s see a complete instruction guide on how to fill out
                  Form 990-EZ.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Organization Details
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section requires information about your organization,
                  including name, address, accounting method, tax-exempt status,
                  and tax period.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/organization-details-img.webp" />
                  <source srcSet="/assets/images/organization-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/organization-details-img.png"
                    loading="lazy"
                    alt="Organization details of Form 990-EZ"
                    width={759}
                    height={247}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  Keeping the previous year&apos;s filed 990-EZ return in hand
                  can help to complete your filing process
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item A - Tax period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This section collects information about your
                  organization&apos;s tax period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select the calendar year option if your organization follows a
                  calendar tax year (January 01 - December 31)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization follows a fiscal tax year, choose the{' '}
                  <b>Tax Period Begin Date</b> and <b>Tax Period End Date</b>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item B - Applicable Checkboxes
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the appropriate boxes to indicate changes on the current
                  return compared with the previously filed 990-EZ return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Address Change</b> - Check the box if the organization
                  changed its address and didn&apos;t report the change on the
                  previously filed Form 990-EZ.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Final Return</b> - Check the box if the organization has
                  terminated its existence or ceased to be a Section 501(a) or
                  Section 527 organization and is filing its final return as an
                  exempt organization or Section 4947(a)(1) trust. You may also
                  need to attach Schedule N along with this final 990-EZ return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Name Change</b> - Check the box if the organization
                  changed its legal name (not its “DBA” name) and didn&apos;t
                  report the change on the previously filed 990-EZ return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must also attach supporting documents for the name change
                  based on your organization&apos;s type.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Amended Return</b> - Check the box if the organization is
                  filing an amended return for the tax year to amend the
                  previously filed return for the same tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must also attach Schedule O and explain which parts,
                  schedules, or attachments of Form 990-EZ were amended and
                  describe those amendments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Initial Return</b> - Check the box if the organization is
                  filing Form 990-EZ return for the first time and hasn&apos;t
                  previously filed any 990 return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b> Application Pending</b> - Check the box if the
                  organization has either filed a Form 1023, 1023-EZ, 1024, or
                  1024-A with the IRS and is awaiting a response, or claims
                  tax-exempt status under section 501(a) but hasn&apos;t filed
                  Form 1023, 1023-EZ, 1024, or 1024-A to be recognized as
                  tax-exempt by the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If this box is checked, you must complete all parts of Form
                  990-EZ and any required schedules.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item C - Organization&apos;s name and address
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the legal name of your organization and its operating
                  address. If the organization operates outside the United
                  States, check the <b>Foreign Address</b> checkbox and enter
                  the address with the country name and postal code.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  If the organization has changed its legal name, follow the
                  instructions in Item B for reporting the name change.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item D - Organization&apos;s EIN
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the Employer Identification Number (EIN) provided for
                  your organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the filing organization has more than one EIN and
                  hasn&apos;t been advised which to use, send notice to
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Department of the Treasury
                  <br /> Internal Revenue Service Center
                  <br /> Ogden, UT 84201-0027
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item F - Group Exemption Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Group Exemption Number (GEN) is a number assigned by the IRS
                  to the central/parent organization of a group that has a group
                  exemption letter.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization is included in a group exemption, enter
                  the group exemption number.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If the organization is covered by a group
                  exemption letter as a subordinate organization, it should file
                  Form 990-EZ only if it isn&apos;t included in a group return
                  filed by the central/parent organization for the tax year.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item G - Accounting Method
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  An accounting method is a practice followed by taxpayers or
                  preparers to report the revenue and expenses of an
                  organization for the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Choose your organization&apos;s accounting method -{' '}
                  <b>Cash, Accrual, or Other</b>. If Other is selected, please
                  explain that accounting method.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item H - Schedule B (Form 990)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box if your organization isn&apos;t required to file
                  Schedule B.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Failure to either check the box in Item H or
                  file Schedule B (Form 990) will result in a determination that
                  the return you file with the IRS is incomplete.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item I - Website
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your organization&apos;s website name and include the
                  website link. If the organization doesn&apos;t maintain a
                  website, enter “N/A” (not applicable).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item J - Tax-Exempt Status
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Tax-exempt status provides an organization with complete
                  relief from paying taxes. In Item J, choose the
                  organization&apos;s tax-exempt status.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization is exempt under section 501(c) (other than
                  501(c)(3)), check the 501(c) box and insert the appropriate
                  subsection number within the parentheses (for example, “2” for
                  a 501(c)(2) organization).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item K - Form of Organization
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-organs-img.webp" />
                  <source srcSet="/assets/images/form-organs-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-organs-img.png"
                    loading="lazy"
                    alt="Form 990-EZ –Line K-Form of Organization, L-Determining Gross Receipts"
                    width={731}
                    height={50}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A Legal Entity Form includes corporations, trusts,
                  unincorporated associations, and other entities (for example,
                  partnerships and limited liability companies (LLCs)) with
                  legal rights and responsibilities, including tax filings.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Choose the organization&apos;s legal entity; whether it&apos;s
                  a <b>Corporation, Trust, Association,</b> or Other. If Other
                  is selected, please explain that form of organization.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Item L - Determining Gross Receipts
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This line determines the organization&apos;s gross receipts.
                  The <b>sum of lines 5b, 6c, and 7b</b> to <b>line 9</b> will
                  be calculated here as the organization&apos;s gross receipts.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If gross receipts calculated on Item L are
                  $200,000 or more and total assets are $500,000 or more, the
                  organization must file Form 990 instead of Form 990-EZ.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.4,
                  }}
                >
                  Part I - Revenue, Expenses, and Changes in Net Assets or Fund
                  Balances
                </Typography>
                <picture>
                  <source srcSet="/assets/images/revenue-img.webp" />
                  <source srcSet="/assets/images/revenue-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/revenue-img.png"
                    loading="lazy"
                    alt="Form 990-ez part-I -Revenue, Expenses, & Changes in Net Assets or Fund Balances"
                    width={760}
                    height={103}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1 - Contributions, Gifts, Grants, and Similar Amounts
                  Received
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In line 1, enter the gross contributions, gifts, grants, and
                  bequests that the organization received from individuals,
                  trusts, corporations, estates, affiliates, foundations, public
                  charities, and other exempt organizations, or raised by an
                  outside professional fundraiser.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2 - Program Service Revenue Including Government Fees and
                  Contracts
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In line 2, enter the total program service revenue (exempt
                  function income) the organization received for the tax year.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Program-related investment income, Unrelated
                  trade or business activities, and government fees and
                  contracts must be included in line 2.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3 - Membership Dues and Assessments
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In line 3 enter the members&apos; and affiliates&apos; dues
                  and assessments that aren&apos;t considered contributions
                  received by the organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The membership benefits can be subscriptions to publications;
                  newsletters (other than one about the organization&apos;s
                  activities only); free or reduced-rate admissions to events
                  sponsored by the organization; use of the organization&apos;s
                  facilities; and discounts on articles or services that both
                  members and nonmembers can buy.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 4 - Investment Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter in line 4 the income earned by the organization from
                  interest payments, gross rents, dividends and interest from
                  securities, and other investment incomes.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5a to 5c - Gains (or Losses) From Sale of Assets Other
                  Than Inventory
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-gross-amount-img.webp" />
                  <source srcSet="/assets/images/990ez-gross-amount-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-gross-amount-img.png"
                    loading="lazy"
                    alt="Line 5a to 5c - Gains (or Losses) From Sale of Assets Other
                  Than Inventory"
                    width={722}
                    height={51}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Lines 5a - Gross amount From Sale of Assets Other Than
                  Inventory
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In Line 5, enter the gross amount from all sales of securities
                  and sales of all other types of investments (real estate,
                  royalty interests, or partnership interests), as well as sales
                  of all other noninventory assets (program-related investments
                  and fixed assets used by the organization in its related and
                  unrelated activities).
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5b - Less: Cost Or Other Basis And Sales Expenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For Line 5b, total the cost or other basis (less depreciation)
                  and selling expenses and enter the result in Line 5b.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 5c - Gains (or Losses) From Sale of Assets Other Than
                  Inventory
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract Line 5b from Line 5a and enter the value in Line 5c.
                  This value will be the Net Gains (or Losses) From Sale of
                  Assets Other Than Inventory.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Do not include on Line 5 any unrealized gains or
                  losses on securities carried in the books of account at market
                  value.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6a to 6d - Gaming and Fundraising Events
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-gaming-img.webp" />
                  <source srcSet="/assets/images/990ez-gaming-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-gaming-img.png"
                    loading="lazy"
                    alt="Line 6a to 6d - Gaming and Fundraising Events"
                    width={740}
                    height={154}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6a - Gross Income from Gaming Events
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In line 6a, report the gross income the organization earned
                  from gaming activities. Also, report in this line the gross
                  income from all gaming activities (other than gaming that is
                  incidental to a fundraising event such as a dinner/dance).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You must attach{' '}
                  <b>
                    <i>Schedule G</i>
                  </b>{' '}
                  (Supplemental Information Regarding Fundraising or Gaming
                  Activities) if the gross income reported in line 6a exceeds
                  $15,000.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6b - Gross Income from Fundraising Events
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In line 6b, enter all fundraising events and activities, such
                  as dinners, dances, carnivals, concerts, sports events,
                  auctions, and door-to-door sales of merchandise.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You must attach{' '}
                  <b>
                    <i>Schedule G</i>
                  </b>{' '}
                  (Supplemental Information Regarding Fundraising or Gaming
                  Activities) if the gross income reported in line 6a exceeds
                  $15,000.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6c - Direct Expenses from Gaming And Fundraising Events
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  In line 6c, enter the direct expenses related to gaming
                  activities and direct expenses attributable to the
                  organization&apos;s provision of goods or services from which
                  it derived gross income at a fundraising event.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 6d - Net Income or (Loss) From Gaming and Fundraising
                  Events
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To calculate the amount in line 6d, add lines 6a and 6b and
                  subtract it from line 6c. This will the organization&apos;s
                  net income or (loss) from gaming and fundraising events
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 7a - Sales of Inventory
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report in line 7a the gross sales (less returns and
                  allowances) of inventory items.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Inventory items</b> generally refer to all the things,
                  goods, merchandise, and materials held by the organization for
                  selling in the market to earn a profit.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-gross-sales-img.webp" />
                  <source srcSet="/assets/images/990ez-gross-sales-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-gross-sales-img.png"
                    loading="lazy"
                    alt="Line 7a - Sales of Inventory"
                    width={731}
                    height={50}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 7b - Cost of Goods Sold
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  On line 7b, report the cost of goods sold related to sales of
                  inventory items reported in line 7a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The usual items included in the <b>cost of goods sold</b> are
                  direct and indirect labor, materials and supplies consumed,
                  freight-in, and a proportion of overhead expenses.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 7c - Gross Profit Or (Loss) From Sales of Inventory
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  To calculate the amount in line 7c, subtract line 7b from line
                  7a. This will be the organization&apos;s gross profit or
                  (loss) from inventory sales.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 8 - Other Revenue
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-other-revenue-img.webp" />
                  <source srcSet="/assets/images/990ez-other-revenue-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-other-revenue-img.png"
                    loading="lazy"
                    alt="Line 8 - Other Revenue"
                    width={740}
                    height={18}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report in line 8 the total income received by the organization
                  from all sources that wasn&apos;t reported on lines 1 to 7.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Examples of line 8 income are interest on notes receivable not
                  held as investments or as program-related investments (defined
                  in the line 2 instructions); interest on loans to officers,
                  directors, trustees, key employees, and other employees; and
                  royalties that aren&apos;t investment income or program
                  service revenue.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You must also attach Schedule O (Supplemental Information) and
                  provide a detailed explanation of all the income reported in
                  line 8.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 9 - Total Revenue
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-total-revenue-img.webp" />
                  <source srcSet="/assets/images/990ez-total-revenue-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-total-revenue-img.png"
                    loading="lazy"
                    alt="Line 9 - Total Revenue"
                    width={725}
                    height={18}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To calculate the total revenue amount in line 9, add lines 1,
                  2, 3, 4, 5c, 6d, 7c, and 8. The added amount will be the
                  organization&apos;s total revenue for the tax year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 10 - Grants and Similar Amounts Paid
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-grants-img.webp" />
                  <source srcSet="/assets/images/990ez-grants-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-grants-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part I, Lines 10–17 showing expense details"
                    width={731}
                    height={135}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report in line 10 the amount of actual grants and similar
                  amounts your organization paid to individuals and
                  organizations. Examples of grants include scholarships,
                  fellowships, and research grants to individuals.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, do not include in line 10 the administrative
                  expenses, purchases of goods or services from affiliates, and
                  membership dues paid to another organization.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 11 - Benefits Paid to or for Members
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization provides benefits to members or
                  dependents (such as organizations exempt under section 501(c)
                  (8), (9), or (17)), enter that amount paid as benefits in line
                  11.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 12 - Salaries, Other Compensation, and Employee Benefits
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report in line 12 the total salaries and wages paid to all
                  officers and employees and payments made to directors and
                  trustees, including compensation reported on Forms W-2 and
                  1099.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also include all other forms of income and benefits received
                  from the organization during the tax year, such as the
                  employer&apos;s share of deferrals (for unfunded plans) and
                  contributions the organization paid to qualified and
                  nonqualified pension and deferred compensation plans, and the
                  employer&apos;s share of contributions to employee benefit
                  programs (such as insurance, health, and welfare programs)
                  that aren&apos;t an incidental part of a pension plan.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 13 - Professional Fees and Other Payments to Independent
                  Contractors
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount of legal, accounting, auditing, other
                  professional fees, and related expenses charged by outside
                  firms and individuals who aren&apos;t employees (independent
                  contractors) of the organization.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If your organization pays $600 or more to
                  persons not treated as employees, you may be required to file
                  <Link
                    href="/information-returns/efile-irs-form-1099-nec/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1099-NEC
                  </Link>
                  , Nonemployee Compensation, or{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-misc/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1099-MISC
                  </Link>
                  , Miscellaneous Income.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 14 - Occupancy, Rent, Utilities, and Maintenance
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter in line 14 the total amount paid or incurred for the use
                  of office space or other facilities. This includes rent,
                  mortgage interest; heat, light, power, and other utilities;
                  outside janitorial services; real estate taxes and property
                  insurance attributable to rental property; and similar
                  expenses.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 15 - Printing, Publications, Postage, and Shipping
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report in line 15, the printing and related costs of producing
                  the filing organization&apos;s newsletters, leaflets, films,
                  and other informational materials, as well as the cost of
                  outside mailing services.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, include the cost of any purchased publications as well
                  as postage and shipping costs not reportable on lines 5b, 6c,
                  or 7b.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 16 - Other Expenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report in line 16 the expenses that aren&apos;t reportable on
                  lines 10 to 15.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For example, expenses such as penalties, fines, and judgments;
                  unrelated business income taxes; insurance, interest,
                  depreciation, and real estate taxes not reported as occupancy
                  expenses; travel and transportation costs; and expenses for
                  conferences, conventions, and meetings can be reported in this
                  line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You must attach also Schedule O (Supplemental Information) and
                  provide a detailed explanation of all the expenses reported in
                  line 16.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 17 - Total Expenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To calculate the total expenses in line 17, add lines 10
                  through 16. The total amount will be the organization&apos;s
                  total expenses for the tax year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 18 - Excess or (Deficit) for the Year
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-excess-img.webp" />
                  <source srcSet="/assets/images/990ez-excess-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-excess-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part I, Lines 18–21 showing net assets"
                    width={725}
                    height={85}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To calculate the excess or deficit amount, calculate the
                  difference between lines 9 and 17 and enter the difference in
                  line 18.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If line 17 is more than line 9, enter the difference in
                  parentheses or as a negative number with a minus sign.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 19 - Net Assets or Fund Balances at Beginning of Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter on line 19 the end-of-year amount from the balance sheet
                  on the prior-year return. The amount entered must agree with
                  the Net assets or fund balances at beginning of year (from
                  line 27, column (A))
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 20 - Other Changes in Net Assets or Fund Balances
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To complete line 20, explain in Schedule O (Form 990) any
                  changes in net assets or fund balances between the beginning
                  and end of the organization&apos;s tax year that aren&apos;t
                  accounted for by the amount on line 18.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 21 - Net Assets or Fund Balances at the End of the Year
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To calculate net assets or fund balances at the end of the
                  year, add lines 18 through 20. The total amount will be the
                  organization&apos;s total net assets or fund balances for the
                  year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part II - Balance Sheets
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-balance-sheet-img.webp" />
                  <source srcSet="/assets/images/990ez-balance-sheet-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-balance-sheet-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part II, Lines 22–27 showing the balance sheet"
                    width={747}
                    height={152}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 22 - Cash, savings, and investments
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In line 22, enter income earned by the organization in all{' '}
                  <b>interest and non-interest-bearing accounts</b>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Income from the book value of securities held as investments,
                  and all other investment holdings including land and buildings
                  held for investment must be reported on Part I, Line 4 and
                  program-related investments must be reported on Part I, Line 2
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 23 - Lands and Buildings
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In line 23, enter the Book Value* (cost or other basis less
                  accumulated depreciation) of all land and buildings owned by
                  the organization and not held for investment.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  *Book Value denotes the value of an asset according to its
                  books or accounts, as reflected on its financial statements.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 24 - Other Assets
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In line 24, enter the total amount of other assets such as
                  accounts receivable, inventories, prepaid expenses, and the
                  organization&apos;s share of assets in any joint ventures,
                  LLCs, and other entities treated as a partnership for federal
                  tax purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, include a description of the assets in <i>Schedule O</i>{' '}
                  (Supplemental Information) and attach it along with Form
                  990-EZ.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 25 - Total Assets
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In line 25 Column (A) enter the amount of the
                  beginning-of-year total assets
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If the end-of-year total assets entered in line
                  25 Column (B) are $500,000 or more, the organization must file
                  Form 990 instead of Form 990-EZ.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 26 - Total Liabilities
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In line 26, enter the organization&apos;s total liabilities
                  recorded for the tax year. It can be accounts payable, grants
                  payable, mortgages or other loans payable, and deferred
                  revenue (revenue received but not yet earned).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, include a description of the liabilities in Schedule O
                  (Supplemental Information) and attach it along with Form
                  990-EZ.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 27 - Net assets or fund balances
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  To determine net assets in line 27, you must subtract line 26
                  (total liabilities) from line 25 (total assets), and enter
                  that amount in line 27.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part III - Statement of Program Service Accomplishments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-statement-img.webp" />
                  <source srcSet="/assets/images/990ez-statement-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-statement-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part III, Lines 28–32 showing program service expenses &  grants"
                    width={712}
                    height={333}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This part collects information about the{' '}
                  <b>program service accomplishments</b> made by your
                  organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Provide a detailed explanation about the organization&apos;s
                  program service accomplishments for each of its three largest
                  program services in lines 28, 29, and 30, and enter the
                  expenses spent on lines 28a, 29a, and 30a respectively.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Also include the services provided, the number of persons
                  benefited, and other relevant information for each program
                  title.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In case your organization has other program services to
                  report, it can be reported in line 31. The organization is
                  required to attach Schedule O (Supplemental Information) and
                  provide a brief explanation of the program services.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part IV - List of Officers, Directors, Trustees, and Key
                  Employees
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-list-officer-img.webp" />
                  <source srcSet="/assets/images/990ez-list-officer-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-list-officer-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part V other information section (Lines 33–36)"
                    width={747}
                    height={466}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This part requires information about any individual who was an
                  officer, director, trustee, or key employee (defined below) of
                  the organization at any time during the organization&apos;s
                  tax year, even if they didn&apos;t receive any compensation
                  from the organization.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 3, fontWeight: 600, fontStyle: 'italic' }}
                >
                  Part IV consists of 5 columns that collect information about
                  the organization&apos;s key employees
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (a) Name and title
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the person&apos;s name and the title or position with
                  the organization at the bottom of the row. If the person had
                  more than one title or position, list all (for instance,
                  president and director).
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> You can only report up to 11 persons on the Part
                  IV table of your 990-EZ return. If more space is needed to
                  enter additional persons, use as many duplicates of the Part
                  IV table as are needed.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (b) Average hours per week devoted to position
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For each person listed in column (a), report an estimate of
                  the average hours per week the person devoted to the
                  organization during the year. Enter “-0-” if applicable.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (c) Reportable compensation (Forms W-2/1099-MISC/
                  1099-NEC) (if not paid, enter -0-)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In this column, enter the person&apos;s reportable
                  compensation. The reportable compensation varies based on the
                  person&apos;s position in the organization.
                </Typography>
                <List sx={{ mb: 1 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      For officers and other key employees—amounts required to
                      be reported in box 1 or 5 of Form W-2 (whichever amount is
                      greater);
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      For directors and individual trustees—amounts required to
                      be reported in box 1 of Form 1099-NEC and/or box 6 of Form
                      1099-MISC for director services and other independent
                      contractor services to the organization, plus box 1 or 5
                      of Form W-2 (whichever amount is greater) if also
                      compensated as an officer or employee; and
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      For institutional trustees (such as banks or trust
                      companies)—fees for services paid under a contractual
                      agreement or statutory entitlement.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (d) Health benefits, contributions to employee benefit
                  plans, and deferred compensation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In column d, report the person&apos;s deferred compensation
                  and benefits.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (e) Estimated amount of other compensation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Here, enter both taxable and nontaxable fringe benefits but
                  don&apos;t include compensation reported in columns (c) or (d)
                  or the following
                </Typography>
                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Working condition fringe benefits that are described in
                      section 132(d).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Expense reimbursements and allowances under an accountable
                      plan are described in Regulations section 1.62-2(c)(2).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      De minimis fringe benefits that are described in section
                      132(e).
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, you can include amounts that the recipients must
                  report as income on their separate income tax returns.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part V - Other Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-other-information-img.webp" />
                  <source srcSet="/assets/images/990ez-other-information-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-other-information-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part V, Lines 38–39 covering loans to officers & Section 501(c)(7) information "
                    width={735}
                    height={247}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 33 - Change in Activities
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select “Yes” if the organization engaged in any significant
                  activity for the tax year that was not previously reported to
                  the IRS. Also, provide a detailed description of each activity
                  in Schedule O <i>(Supplemental Information)</i>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, If the organization has never filed a Form 990-EZ,
                  select “No” on line 33.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 34 - Changes in Organizing or Governing Documents
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select &quot;Yes&quot; if the organization made any
                  significant changes to the organizing or governing documents.
                  Also, attach a conformed copy of the amended documents if they
                  reflect a change to the organization&apos;s name and describe
                  the significant changes on Schedule O (Supplemental
                  Information).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Lines 35a and 35b - Unrelated Business Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select &quot;Yes&quot; in line 35a if the organization has an
                  unrelated business gross income of $1,000 or more during the
                  year from business activities that were reported on lines 2,
                  6a, and 7a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have selected “Yes” in line 35a, and filed Form 990-T
                  for the tax year, select “Yes” in line 35b. If not, select
                  “No” and explain in Schedule O (Supplemental Information)
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 35c - Section 6033(e) Tax for Lobbying Expenditures
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select &quot;Yes&quot; if the organization comes under section
                  501(c)(4), 501(c)(5), or 501(c)(6) organization subject to
                  section 6033(e) notice, reporting, and proxy tax requirements
                  during the year, and complete Schedule C, Part III and attach
                  it along with the Form 990-EZ return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization selects “No” to line 35c, it is certifying
                  that it wasn&apos;t subject to the notice and reporting
                  requirements of section 6033(e) and that the organization had
                  no lobbying and political expenditures potentially subject to
                  the proxy tax.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 36 - Liquidation, Dissolution, Termination, or
                  Significant Disposition of Net Assets
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select “Yes” if the organization underwent a liquidation,
                  dissolution, termination, or significant disposition of net
                  assets during the year and attach applicable parts of{' '}
                  <i>
                    Schedule N (Liquidation, Termination, Dissolution, or
                    Significant Disposition of Assets)
                  </i>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  A “significant disposition of net assets” is a sale, exchange,
                  disposition, or other transfer of more than 25% of the FMV of
                  the organization&apos;s net assets during the year, regardless
                  of whether the organization received full or adequate
                  consideration.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A significant disposition of net assets involves:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    One or more dispositions during the organization&apos;s tax
                    year amounting to more than 25% of the FMV of the
                    organization&apos;s assets as of the beginning of its tax
                    year; or
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    One of a series of related dispositions or events commenced
                    in a prior year that, when combined, comprise more than 25%
                    of the FMV of the organization&apos;s assets as of the
                    beginning of the tax year when the first disposition of net
                    assets occurred. Whether a series of related dispositions is
                    a significant disposition of net assets depends on the facts
                    and circumstances in each case
                  </li>
                </ol>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 37 - Expenditures for Political Purposes
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-political-img.webp" />
                  <source srcSet="/assets/images/990ez-political-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-political-img.png"
                    loading="lazy"
                    alt="Line 37 - Expenditures for Political Purposes"
                    width={747}
                    height={35}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In line 37a, enter the amount of political expenditures;
                  direct or indirect made by the organization for the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b>
                    <i> Political expenditures</i>
                  </b>{' '}
                  are intended to influence the selection, nomination, election,
                  or appointment of anyone to a federal, state, or local public
                  office, or office in a political organization, or the election
                  of Presidential or Vice Presidential electors.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  It includes a payment, distribution, loan, advance, deposit,
                  gift of money, or anything of value. It also includes a
                  contract, promise, or agreement to make an expenditure,
                  whether or not legally enforceable.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 38 - Loans to or From Officers, Directors, Trustees, and
                  Key Employees
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-director-img.webp" />
                  <source srcSet="/assets/images/990ez-director-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-director-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part V, Lines 38–39 covering loans to officers & Section 501(c)(7) information"
                    width={736}
                    height={103}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select &quot;Yes&quot; in line 38a if the organization borrows
                  from, or makes any loans to, any officer, director, trustee,
                  or key employee; or were any such loans made in a prior year
                  and still outstanding at the end of the tax year covered by
                  this return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report any interest expense paid to an officer, director,
                  trustee, or key employee on line 16 (except for mortgage
                  interest reportable on line 14) and any interest income paid
                  by an officer, director, trustee, or key employee on line 8.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If selected Yes in line 38a, complete Schedule L, Part II, and
                  enter in line 38b the total amount involved.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 39 - Section 501(c)(7) Organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  On line 39a, enter the capital contributions, initiation fees,
                  and unusual amounts of income not included in figuring gross
                  receipts to determine the exempt status of section 501(c)(7)
                  organizations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  On line 39b, the gross receipts for public use of club
                  facilities are gross receipts (as defined above for 501(c)(7)
                  exemption purposes) derived from the use of the
                  organization&apos;s facilities by persons other than members,
                  spouses of members, dependents of members, or guests of
                  members.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 40a - Section 501(c)(3) Organizations: Disclosure of
                  Excise Taxes Imposed Under Section 4911, 4912, or 4955
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-section-org-img.webp" />
                  <source srcSet="/assets/images/990ez-section-org-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-section-org-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part V, Line 40 covering excise taxes, excess benefit transactions, & prohibited tax shelter disclosures"
                    width={731}
                    height={197}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization comes under Section{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    501(c)(3)
                  </Link>
                  , it must disclose any excise tax imposed during the year
                  under Section 4911 (excess lobbying expenditures); 4912
                  (disqualifying lobbying expenditures); or, unless abated, 4955
                  (political expenditures).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 40b - Section 501(c)(3), 501(c)(4), and 501(c)(29)
                  Organizations: Disclosure of Section 4958 Excess Benefit
                  Transactions and Excise Taxes
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select &quot;Yes&quot; if the organization became aware,
                  before filing this return, that it engaged in an excess
                  benefit transaction with a disqualified person in the current
                  tax year or a prior year, and if the transaction hasn&apos;t
                  been reported on any of the organization&apos;s prior Form
                  990-EZ.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, complete Schedule L, Part I, and attach it along with
                  the 990-EZ return.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 40c - Taxes Imposed on Organization Managers or
                  Disqualified Persons
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization comes under Section 501(c)(3), 501(c)(4),
                  and 501(c)(29), enter in line 40c the amount of taxes imposed
                  on organization managers and/or disqualified persons under
                  sections 4912, 4955, and 4958, unless abated.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 40d - Taxes Reimbursed by the Organization
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If your organization comes under Section 501(c)(3), 501(c)(4),
                  and 501(c)(29), enter the amount of tax on line 40c that was
                  reimbursed by the organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Any reimbursement of the excise tax liability of a
                  disqualified person or organization manager will be treated as
                  an excess benefit unless:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    The organization treats the reimbursement as compensation
                    during the year the reimbursement is made; and
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    The total compensation to that person, including the
                    reimbursement, is reasonable.
                  </li>
                </ol>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 40e - Tax on Prohibited Tax Shelter Transactions
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select “Yes” if the organization was a party to a prohibited
                  tax shelter transaction as described in section 4965(e) at any
                  time during the organization&apos;s tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You must complete <b>Form 8886-T</b>, Disclosure by Tax-Exempt
                  Entity Regarding Prohibited Tax Shelter Transaction, and may
                  also have to file Form 4720 and pay excise tax imposed by
                  section 4965.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 41 - List of States
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-state-list-img.webp" />
                  <source srcSet="/assets/images/990ez-state-list-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-state-list-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part V, Lines 41–43 state filings & foreign account disclosures"
                    width={755}
                    height={203}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  On this line, provide the names of states with which a copy of
                  this return is filed.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 42a - Location of Books and Records
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  On line 42a, provide the name of the person who possesses the
                  organization&apos;s books and records.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 42b - Foreign Financial Accounts
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select “Yes” in line 42b if either any one of the two
                  conditions applies
                </Typography>
                <ol style={{ marginBottom: 28 }}>
                  <li style={{ marginBottom: 16 }}>
                    At any time during the calendar year ending with or within
                    the organization&apos;s tax year, the organization had an
                    interest in, or signature or other authority over, a
                    financial account in a foreign country (such as a bank
                    account, securities account, or other financial account);
                    and
                  </li>
                  <ul style={{ marginBottom: 16 }}>
                    <li style={{ marginBottom: 16 }}>
                      The combined value of the accounts was more than $10,000
                      at any time during the calendar year; and
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      The accounts weren&apos;t with a U.S. military banking
                      facility operated by a U.S. financial institution.
                    </li>
                  </ul>
                  <li style={{ marginBottom: 16 }}>
                    The organization owns more than 50% of the stock in any
                    corporation that would answer “Yes” to item 1 above.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If “Yes,” enter the name of the foreign country or countries,
                  and continue on Schedule O if more space is needed. Also, file
                  FinCEN Form 114, Report of Foreign Bank and Financial Accounts
                  (FBAR), electronically with the Department of the Treasury
                  using FinCEN&apos;s BSA E-Filing System.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 43 - Section 4947(a)(1) Nonexempt Charitable Trusts
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you&apos;re a Section 4947(a)(1) nonexempt charitable trust
                  filing Form 990-EZ instead of Form 1041, check the box and
                  enter the amount of tax-exempt interest received or accrued
                  during the tax year.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Section 4947(a)(1) nonexempt charitable trusts
                  must complete all sections of the Form 990-EZ and schedules
                  that 501(c)(3) organizations must complete. All references to
                  a section 501(c)(3) organization in the Form 990-EZ,
                  schedules, and instructions include a section 4947(a)(1) trust
                  (for instance, such a trust must complete Schedule A (Form
                  990)) unless expressly excepted.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 44a - Donor Advised Funds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-maintain-img.webp" />
                  <source srcSet="/assets/images/990ez-maintain-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-maintain-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part V Line 44 showing eligibility questions and compliance disclosures"
                    width={754}
                    height={134}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select “Yes” if the organization maintained any donor-advised
                  funds during the year. If yes, the organization must file Form
                  990 instead of Form 990-EZ.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A “donor advised fund” is a fund or account:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    That is separately identified by reference to contributions
                    of a donor or donors,
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    That is owned and controlled by a sponsoring organization,
                    and
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Over which the donor or donor advisor has or reasonably
                    expects to have advisory privileges in the distribution or
                    investment of amounts held in the donor advised fund or
                    account because of the donor&apos;s status as a donor.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 44b - Hospital Facilities
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select “Yes” if the organization operated one or more hospital
                  facilities during the tax year. If yes, the organization must
                  file Form 990 instead of Form 990-EZ.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A “hospital facility” is a facility that is required to be
                  licensed, registered, or similarly recognized by a state as a
                  hospital. This includes a hospital that is operated through a
                  disregarded entity or joint venture treated as a partnership
                  for federal tax purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  However, it doesn&apos;t include hospitals that are located
                  outside the United States or hospitals that are operated by
                  entities organized as separate legal entities from the
                  organization that are treated as corporations for federal tax
                  purposes.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Lines 44c and 44d - Payments for Indoor Tanning Services
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select “Yes” for line 44c if the organization received any
                  payments during the year for indoor tanning services.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If “Yes” is selected in line 44c, select &quot;Yes&quot; in
                  line 44d if you the organization filed Form 720 to report the
                  payments received for indoor tanning services. If “No,”
                  explain Schedule O (Supplemental Information)
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If an organization receives payment for services
                  for indoor tanning services during the year, it must collect
                  from the recipient of the services a tax equal to 10% of the
                  amount paid for such service, whether paid by insurance or
                  otherwise and remit such tax quarterly to the IRS by filing{' '}
                  <i>Form 720, Quarterly Federal Excise Tax Return</i>.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 45a - Section 512(b)(13) Controlled Entity
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-controlled-img.webp" />
                  <source srcSet="/assets/images/990ez-controlled-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-controlled-img.png"
                    loading="lazy"
                    alt="Line 45a - Section 512(b)(13) Controlled Entity"
                    width={723}
                    height={64}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select “Yes” if the organization had a controlled entity
                  within the meaning of section 512(b)(13) during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A “
                  <b>
                    controlled entity within the meaning of section 512(b)(13)
                  </b>
                  ” may be a stock or nonstock corporation, association,
                  partnership, LLC, or trust of which the controlling
                  organization owns more than 50% of:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    The stock of a corporation (measured by voting power or
                    value),
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    The profits or capital interest in a partnership, or
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    The beneficial interest in a trust or other entity.
                  </li>
                </ul>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 45b - Transactions With a Section 512(b) (13) Controlled
                  Entity
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select &quot;Yes&quot; if the organization received any
                  payment from or engaged in any transaction with a controlled
                  entity within the meaning of section 512(b)(13)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If “Yes,” the organization must file Form 990 and Schedule R
                  (Related Organizations and Unrelated Partnerships) instead of
                  Form 990-EZ.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 46 - Political Campaign Activities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-candidates-img.webp" />
                  <source srcSet="/assets/images/990ez-candidates-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-candidates-img.png"
                    loading="lazy"
                    alt="Line 46 - Political Campaign Activities"
                    width={753}
                    height={52}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select &quot;Yes&quot; if the organization engaged, directly
                  or indirectly, in political campaign activities on behalf of
                  or in opposition to candidates for public office.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If yes, complete the applicable parts on Schedule C (Political
                  Campaign and Lobbying Activities), Part I, and attach it along
                  with your 990-EZ return.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part VI - Section 501(c)(3) Organizations Only
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-part-6-organs-img.webp" />
                  <source srcSet="/assets/images/990ez-part-6-organs-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-part-6-organs-img.png"
                    loading="lazy"
                    alt="Part VI - Section 501(c)(3) Organizations Only"
                    width={748}
                    height={68}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <i>
                    <span style={{ fontWeight: 600 }}>
                      All section{' '}
                      <Link
                        href="/resources/tax-exempt/501c3-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        501(c)(3) organizations
                      </Link>
                    </span>{' '}
                    (including, for purposes of Form 990-EZ, section 4947(a)(1)
                    nonexempt charitable trusts) must complete Part VI
                  </i>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  So if your organization falls under Section 501(c)(3), you
                  must answer questions 47–49b and 52, and complete the tables
                  for lines 50 and 51.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 47 - Lobbying Activities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-engage-img.webp" />
                  <source srcSet="/assets/images/990ez-engage-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-engage-img.png"
                    loading="lazy"
                    alt="Form 990-EZ Part VI, Lines 47–49 showing lobbying activities, school status,& transfers to related organizations"
                    width={729}
                    height={99}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select Yes if your organization engaged in lobbying activities
                  or has a section 501(h) election in effect during the tax
                  year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The organization must complete{' '}
                  <i>Schedule C (Political Campaign and Lobbying Activities)</i>
                  , Part II-A, regardless of whether they engaged in lobbying
                  activities during the tax year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 48 - Schools
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select Yes if your organization is described as a school in
                  section 170(b)(1)(A)(ii) and complete{' '}
                  <i>Schedule E (Schools)</i>.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 49 - Transfers to Exempt Non-Charitable Related
                  Organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select “Yes” in line 49a if the organization made any transfer
                  to a related organization that is exempt other than a
                  501(c)(3) organization, such as a related 501(c)(4)
                  organization or a related 527 political organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The transfer the organization made can be anything of value
                  (cash, other assets, services, use of property, etc.) to the
                  exempt non-charitable related organization, whether or not for
                  adequate consideration.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 50 - Five Highest Compensated Employees Over $100,000
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-completed-table-img.webp" />
                  <source srcSet="/assets/images/990ez-completed-table-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-completed-table-img.png"
                    loading="lazy"
                    alt="Line 50 - Five Highest Compensated Employees Over $100,000"
                    width={741}
                    height={263}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization has employees (other than officers,
                  directors, trustees, and key employees as defined in the Part
                  IV instructions, earlier) with the highest annual compensation
                  of over $100,000, provide the details of the first five highly
                  compensated employees and complete line 50.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (a) Name and title of each employee
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the employee&apos;s name and the title or position with
                  the organization at the bottom of the row.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (b) Average hours per week devoted to position
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For each employee listed in column (a), report an estimate of
                  the average hours per week they devoted to the organization
                  during the year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (c) Reportable compensation (Forms W-2/1099-MISC/
                  1099-NEC)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In column c, enter the employee&apos;s reportable
                  compensation.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (d) Health benefits, contributions to employee benefit
                  plans, and deferred compensation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In column d, report the employee’s deferred compensation and
                  benefits.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (e) Estimated amount of other compensation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  In column e, enter the employee&apos;s estimated amount of
                  other compensation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  On line 50f, enter the number of other employees (other than
                  officers, directors, trustees, and key employees) with annual
                  compensation over $100,000 who weren&apos;t individually
                  listed on line 50.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 51 - Five Highest Compensated Independent Contractors
                  Over $100,000
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-compensated-img.webp" />
                  <source srcSet="/assets/images/990ez-compensated-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-compensated-img.png"
                    loading="lazy"
                    alt=" Line 51 - Five Highest Compensated Independent Contractors
                  Over $100,000"
                    width={734}
                    height={243}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If your organization has independent contractors who received
                  more than $100,000 in compensation for services, whether
                  professional services or other services, from the
                  organization, provide the details of the first five highly
                  compensated employees and complete line 51.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  On line 51d, enter the number of other independent contractors
                  receiving over $100,000 who weren&apos;t individually listed
                  on line 51.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (a) Name and business address of each independent
                  contractor
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the independent contractor&apos;s name and business
                  address at the bottom of the row.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (b) Type of service
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Here, enter details of the services provided by the
                  independent contractors.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Column (c) Compensation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter here the amount of compensation the organization paid,
                  whether reported in box 1 of Form 1099-NEC and/or box 6 of
                  Form 1099-MISC or paid under the parties&apos; agreement or
                  applicable state law, for the calendar year ending with or
                  within the organization&apos;s tax year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 52 - Schedule A
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-schedule-img.webp" />
                  <source srcSet="/assets/images/990ez-schedule-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-schedule-img.png"
                    loading="lazy"
                    alt="Line 52 - Schedule A"
                    width={739}
                    height={63}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select Yes if the organization comes under section 501(c)(3)
                  organization, and complete and attach{' '}
                  <i>Schedule A (Public Charity Status and Public Support)</i>.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Signature Block
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990ez-sign-here-img.webp" />
                  <source srcSet="/assets/images/990ez-sign-here-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990ez-sign-here-img.png"
                    loading="lazy"
                    alt="Signature Block"
                    width={747}
                    height={149}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  After completing the return, it must be signed by the current
                  president, vice president, treasurer, assistant treasurer,
                  chief accounting officer, or other corporate officer (such as
                  tax officer) who is authorized to sign as of the date this
                  return is filed.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Paid Preparer
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This section is for the Paid preparer who was paid to prepare
                  the return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you&apos;re a paid preparer, you must sign the return, list
                  the preparer taxpayer identification number (PTIN), and fill
                  in the other blanks in the Paid Preparer Use Only area.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The paid preparer must:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Sign the return in the space provided for the
                    preparer&apos;s signature;
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the preparer&apos;s information (including the
                    preparer’s PTIN and the preparer firm’s EIN, if applicable);
                    and
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Give a copy of the return to the organization.
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="file"
                >
                  How to file Form 990-EZ?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  You have two options to file your Form 990-EZ return with the
                  IRS - efficient e-filing or traditional paper filing.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Form 990-EZ E-filing (recommended)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The IRS recommends e-filing your 990-EZ for a faster and
                  simpler experience. In addition, you&apos;ll get instant
                  notifications when they&apos;re processed by the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Choosing an IRS-authorized e-file provider like TaxZerone
                  makes it easy to{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    file Form 990 EZ online
                  </span>
                  . With just a few clicks, you can fill out your 990-EZ return,
                  review it, and transmit it to the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  It&apos;s that quick and easy! Choose e-filing for a smooth
                  and efficient 990-EZ filing experience.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you choose to paper-file Form 990-EZ, here are the steps
                  you need to follow
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b> Obtain the Form:</b> Download and print Form 990-EZ from
                    the IRS website.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Complete Form:</b> Fill out each section of Form 990-EZ,
                    providing accurate information about your
                    organization&apos;s activities, finances, governance, and
                    compliance with tax laws. Use the instructions provided by
                    the IRS to guide you through the process.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Attach Schedules if Necessary:</b> Depending on your
                    organization&apos;s activities and financial transactions,
                    you may need to attach additional schedules or supporting
                    documents to Form 990-EZ.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Calculate and Include Payment: </b> If your organization
                    owes taxes based on the information provided on Form 990-EZ,
                    calculate the amount owed and include payment with the
                    submission. Payments can be made by check or money order
                    payable to the &quot;United States Treasury.&quot;
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Sign and Date the Form: </b> The form must be signed and
                    dated by an authorized individual within the organization,
                    such as the president, treasurer, or another authorized
                    officer.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Mail the Form: </b> Once completed, mail the signed Form
                    990-EZ and any accompanying documents to the IRS mailing
                    address.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Keep Copies for Your Records: </b> Make copies of the
                    completed Form 990-EZ and all supporting documents for your
                    organization&apos;s records. These documents may be needed
                    for future reference or in the event of an audit.
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> While the IRS previously allowed paper filing
                  for Form 990-EZ, there has been a change in recent years. As
                  for tax years ending after July 31, 2021, electronic filing is
                  mandatory for all Form 990-EZ submissions.
                  <br />
                  However, if your organization&apos;s tax year ended before
                  August 1, 2021, you can still file a paper Form 990-EZ.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollmarginTop: 63,
                  }}
                  id="whereto"
                >
                  Where to mail 990 EZ tax form?
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Send a paper copy of Form 990-EZ to the mailing address
                  mentioned below:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Department of the Treasury,
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Internal Revenue Service Center,
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Ogden, UT 84201-0027.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  If your organization’s principal business, office, or agency
                  is located in a foreign country or a U.S. possession, mail the
                  form to:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Internal Revenue Service Center,
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  P.O. Box 409101,
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Ogden, UT 84409.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollmarginTop: 83,
                  }}
                  id="e-file"
                >
                  How to e-file Form 990-EZ?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  E-filing your Form 990-EZ takes just 3 steps! To make it even
                  faster, gather all the necessary information (like employee
                  details and compensation amounts) before you start. That way,
                  you can complete your filing process in no time.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Information required to file Form 990-EZ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organization&apos;s details, EIN, tax year, accounting method,
                  and exempt status
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Once you have this information ready, you can follow the steps
                  below to e-file Form 990-EZ using TaxZerone.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1</b> - Enter the required information, such as your
                  organization&apos;s details, EIN, tax year, accounting method,
                  and exempt status.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2</b> - Complete all the parts provided in the form
                  and attach the required schedules.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3</b> - Review the information you provided in the
                  form, check and rectify the errors if any, and transmit the
                  Form 990-EZ return to the IRS.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollmarginTop: 83,
                  }}
                  id="with"
                >
                  E-file Form 990-EZ with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  When you choose to{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 990-EZ
                  </Link>{' '}
                  with TaxZerone, you can enjoy benefits such as
                </Typography>

                <List sx={{ mb: 1 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>IRS-authorized:</b> File your 990-EZ return through an
                      IRS-authorized e-file service provider.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>IRS form validations:</b> catch any errors before you
                      submit.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Easy E-filing:</b> Simple navigating and a
                      user-friendly interface to e-file Form 990-EZ within
                      minutes.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Secure E-filing:</b> Your 990-EZ return will be
                      submitted securely to the IRS.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> Top value:</b> Enjoy the industry&apos;s best price,
                      starting at just $89.99 per return.
                    </ListItemText>
                  </ListItem>
                </List>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on official IRS
                        resources, including the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990ez.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Instructions
                        </Link>{' '}
                        for{' '}
                        <span style={{ fontWeight: 600 }}> Form 990-EZ</span>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i8868.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Instructions
                        </Link>{' '}
                        <span style={{ fontWeight: 600 }}>for Form 8868</span>.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File Form 990-EZ with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Stay compliant and save time by e-filing your Form 990-EZ securely
              and effortlessly!
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990EZinstructions`}
              >
                E-file Form 990-EZ
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form990EZInstructions;
